/**
 * Utah's own lines on the 2-minute check guides (checkguides.ts; library.ts adds them for Utah places): the rule that makes the Food
 * Code Utah's law, and the number Salt Lake County writes on its report for the same section (SLCO_CODE_FDA, derived from the
 * county's own rationale labels; Health Regulation #5). A guide whose Food Code sections the county book does not map gets no
 * county line (never guessed).
 */
import type { LibraryTopic } from "../../library.ts";
import { SLCO_CODE_FDA } from "./official.ts";

/** R392-100-4 adopts the 2022 FDA Food Code (states/ut/official.ts). */
export const UT_FOOD_CODE_ADOPTION = "R392-100-4: the 2022 FDA Food Code is Utah's rule";

/** "Food Code 3-501.16(A)(2)" → "3-501.16". */
const sectionOf = (cite: string) => /^Food Code (\d-\d{3}\.\d+)/.exec(cite)?.[1] ?? null;

/** Salt Lake County's codes for the Food Code sections a guide cites, in the county's order. */
export function slcoCodesFor(topic: Pick<LibraryTopic, "sections">): string[] {
  const secs = new Set(topic.sections.flatMap((s) => s.citations.map(sectionOf).filter((x): x is string => !!x)));
  return Object.entries(SLCO_CODE_FDA).filter(([, v]) => secs.has(v.fda)).map(([code]) => code);
}

/** The guide with Utah's lines added to its first section ("What the inspector looks for"). */
export function withUtahCites(topic: LibraryTopic): LibraryTopic {
  const codes = slcoCodesFor(topic);
  const extra = [UT_FOOD_CODE_ADOPTION, ...(codes.length ? [`Salt Lake County Reg. #5 §${codes.join(", §")}`] : [])];
  return { ...topic, sections: topic.sections.map((s, i) => (i === 0 ? { ...s, citations: [...s.citations, ...extra] } : s)) };
}
